import type { ResumeData } from "@/types/resume";
import { Mail, Phone, MapPin, Globe, Linkedin, Github } from "lucide-react";
import { formatDate, getOrderedSections } from "./templateUtils";
import { bodyFont, headingFont } from "./useResumeFonts";

export function TemplateTechnicalClean({ data, overridePrimary }: { data: ResumeData; overridePrimary?: string }) {
  const { personalInfo: p } = data;
  const PRIMARY = overridePrimary || "#0ea5e9";
  const FONT_BODY = bodyFont(data, "'JetBrains Mono', 'Fira Code', monospace");
  const FONT_HEADING = headingFont(data, FONT_BODY);

  const sectionRenderers: Record<string, React.ReactNode> = {
    skills: data.skills.length > 0 ? (
      <Section color={PRIMARY} key="skills" title="// SKILLS">
        <div className="grid grid-cols-2 gap-2">
          {data.skills.map((g) => (
            <div key={g.id}>
              {g.category && <span className="font-bold text-[10px]" style={{ color: PRIMARY }}>{g.category}</span>}
              <p className="text-[10px]">{g.skills.filter(Boolean).join(", ")}</p>
            </div>
          ))}
        </div>
      </Section>
    ) : null,
    summary: data.summary ? <Section color={PRIMARY} key="summary" title="// ABOUT"><p>{data.summary}</p></Section> : null,
    workExperience: data.workExperience.length > 0 ? (
      <Section color={PRIMARY} key="workExperience" title="// EXPERIENCE">
        {data.workExperience.map((exp) => (
          <div key={exp.id} className="mb-3 last:mb-0">
            <div className="flex justify-between"><div><strong>{exp.jobTitle}</strong>{exp.company && <span style={{ color: PRIMARY }}> @ {exp.company}</span>}</div><span className="text-[10px]" style={{ color: "#475569" }}>{exp.startDate && formatDate(exp.startDate)} — {exp.currentlyWorking ? "Present" : exp.endDate ? formatDate(exp.endDate) : ""}</span></div>
            {exp.responsibilities.filter(Boolean).length > 0 && <ul className="list-disc ml-4 mt-1 space-y-0.5">{exp.responsibilities.filter(Boolean).map((r, i) => <li key={i}>{r}</li>)}</ul>}
          </div>
        ))}
      </Section>
    ) : null,
    projects: data.projects.length > 0 ? (
      <Section color={PRIMARY} key="projects" title="// PROJECTS">
        {data.projects.map((proj) => (
          <div key={proj.id} className="mb-2 last:mb-0">
            <strong>{proj.title}</strong>{proj.role && <span> — {proj.role}</span>}
            {proj.technologies.filter(Boolean).length > 0 && <p className="text-[10px]" style={{ color: PRIMARY }}>[{proj.technologies.join(", ")}]</p>}
            {proj.description && <p className="mt-0.5">{proj.description}</p>}
          </div>
        ))}
      </Section>
    ) : null,
    education: data.education.length > 0 ? (
      <Section color={PRIMARY} key="education" title="// EDUCATION">
        {data.education.map((edu) => (
          <div key={edu.id} className="mb-1 last:mb-0">
            <strong>{edu.degree}</strong>{edu.institution && <span> — {edu.institution}</span>}{edu.location && <span> — {edu.location}</span>}{edu.graduationDate && <span className="text-[10px] ml-2" style={{ color: "#475569" }}>{formatDate(edu.graduationDate)}</span>}
          </div>
        ))}
      </Section>
    ) : null,
    certifications: data.certifications.length > 0 ? <Section color={PRIMARY} key="certifications" title="// CERTIFICATIONS">{data.certifications.map((c) => <p key={c.id}><strong>{c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ color: PRIMARY }}>{c.name}</a> : c.name}</strong>{c.issuer ? ` — ${c.issuer}` : ""}</p>)}</Section> : null,
    languages: data.languages.length > 0 ? <Section color={PRIMARY} key="languages" title="// LANGUAGES"><p>{data.languages.map((l) => `${l.language} (${l.proficiency})`).join(", ")}</p></Section> : null,
    achievements: data.achievements.filter(Boolean).length > 0 ? <Section color={PRIMARY} key="achievements" title="// ACHIEVEMENTS"><ul className="list-disc ml-4 space-y-0.5">{data.achievements.filter(Boolean).map((a, i) => <li key={i}>{a}</li>)}</ul></Section> : null,
  };

  return (
    <div className="p-6 text-xs leading-relaxed" style={{ fontFamily: FONT_BODY, color: "#1e293b", background: "#ffffff" }}>
      <div className="mb-4">
        {p.fullName && <h1 className="text-lg font-bold" style={{ fontFamily: FONT_HEADING }}>{p.fullName}</h1>}
        {p.professionalTitle && <p className="text-sm" style={{ color: PRIMARY }}>{p.professionalTitle}</p>}
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[10px]" style={{ color: "#475569" }}>
          {p.email && <span className="flex items-center gap-0.5"><Mail className="w-2.5 h-2.5" />{p.email}</span>}
          {p.phone && <span className="flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{p.phone}</span>}
          {p.city && <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{p.city}</span>}
          {p.github && <span className="flex items-center gap-0.5"><Github className="w-2.5 h-2.5" />{p.github}</span>}
          {p.linkedin && <span className="flex items-center gap-0.5"><Linkedin className="w-2.5 h-2.5" />{p.linkedin}</span>}
          {p.portfolio && <span className="flex items-center gap-0.5"><Globe className="w-2.5 h-2.5" />{p.portfolio}</span>}
        </div>
      </div>
      {getOrderedSections(data).map((key) => sectionRenderers[key])}
    </div>
  );
}

function Section({ title, children, color = "#0ea5e9" }: { title: string; children: React.ReactNode; color?: string }) {
  return (
    <div className="mb-4">
      <h2 className="text-xs font-bold mb-1.5 pb-1" style={{ color, borderBottom: "1px solid #e2e8f0" }}>{title}</h2>
      {children}
    </div>
  );
}
